/**
 * @fileoverview Componente para previsualización de archivos PDF y otros tipos de archivo.
 * Usa el componente PreviewFile existente para PDFs y fallback para otros tipos.
 * 
 * @module FilePreviewMC
 * @category Component - Molecule
 * @since 1.0.0
 * @version 1.0.0
 */

import React, { memo, useState, useCallback, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PreviewFile from '../../components/PreviewFile';
import LoadingMC from '../../components/LoadingMC';

/**
 * Componente para mostrar preview de archivos (PDFs y otros tipos).
 * 
 * @component FilePreviewMC
 * @category Component - Molecule
 * 
 * @description
 * Maneja la previsualización de diferentes tipos de archivo:
 * - PDFs: Usa PreviewFile para mostrar la primera página
 * - Otros archivos: Muestra ícono y enlace de descarga
 * 
 * **Optimizaciones de performance:**
 * - React.memo para evitar re-renders innecesarios
 * - useCallback para funciones estables
 * - useMemo para cálculos costosos
 * - useEffect optimizado con dependencias específicas
 * 
 * @param {FilePreviewMCProps} props - Propiedades del componente
 * @returns {JSX.Element} Preview del archivo o fallback
 */
const FilePreviewMC = memo(({ fileUrl, fileName }) => {
  const { t } = useTranslation('FastAnswer');
  const [pdfFile, setPdfFile] = useState(null);
  const [showFallback, setShowFallback] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Convierte una URL en un objeto File memoizada para evitar recreaciones
   * @function urlToFile
   */
  const urlToFile = useCallback(async (url, filename = 'document.pdf', mimeType = 'application/pdf') => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      return new File([blob], filename, { type: mimeType });
    } catch (error) {
      console.error('Error converting URL to File:', error);
      return null;
    }
  }, []);

  /**
   * Detecta si el archivo es PDF - memoizado para evitar recálculos
   * @type {boolean}
   */
  const isPdf = useMemo(() => {
    return fileUrl?.toLowerCase().includes('.pdf') || fileName?.toLowerCase().includes('.pdf');
  }, [fileUrl, fileName]);

  /**
   * Maneja la carga del archivo PDF
   * @function handlePdfLoad
   */
  const handlePdfLoad = useCallback(async () => {
    if (!fileUrl) return;
    
    setIsLoading(true);
    setShowFallback(false);
    setPdfFile(null);

    try {
      const file = await urlToFile(fileUrl, fileName || 'document.pdf', 'application/pdf');
      if (file) {
        setPdfFile(file);
      } else {
        setShowFallback(true);
      }
    } catch (error) {
      console.error('Error loading PDF:', error);
      setShowFallback(true);
    } finally {
      setIsLoading(false);
    }
  }, [fileUrl, fileName, urlToFile]);

  /**
   * Effect optimizado para cargar PDFs solo cuando cambian las dependencias críticas
   */
  useEffect(() => {
    if (isPdf && fileUrl) {
      handlePdfLoad();
    } else {
      // Para archivos que no son PDF, mostrar fallback inmediatamente
      setIsLoading(false);
      setShowFallback(true);
      setPdfFile(null);
    }
  }, [isPdf, fileUrl, handlePdfLoad]);

  /**
   * Componente de fallback para PDFs - memoizado para evitar re-renders
   * @function PdfFallback
   */
  const PdfFallback = useMemo(() => (
    <div className="tw-w-48 tw-h-32 tw-rounded tw-border tw-bg-gray-50 tw-flex tw-flex-col tw-items-center tw-justify-center tw-p-4">
      <svg className="tw-w-8 tw-h-8 tw-text-red-400 tw-mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
      <span className="tw-text-xs tw-text-gray-600 tw-mb-2 tw-text-center">PDF</span>
      <a 
        href={fileUrl} 
        target="_blank" 
        rel="noopener noreferrer"
        className="tw-text-sm tw-text-blue-600 tw-hover:text-blue-800 tw-text-center tw-underline"
      >
        {t('viewPdf') || 'Ver PDF'}
      </a>
    </div>
  ), [fileUrl, t]);

  /**
   * Componente para archivos no-PDF - memoizado
   * @function FileFallback
   */
  const FileFallback = useMemo(() => (
    <div className="tw-w-48 tw-h-32 tw-rounded tw-border tw-bg-gray-50 tw-flex tw-flex-col tw-items-center tw-justify-center tw-p-4">
      <svg className="tw-w-8 tw-h-8 tw-text-gray-400 tw-mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
      <a 
        href={fileUrl} 
        target="_blank" 
        rel="noopener noreferrer"
        className="tw-text-sm tw-text-blue-600 tw-hover:text-blue-800 tw-text-center tw-underline"
      >
        {t('downloadFile') || 'Descargar archivo'}
      </a>
    </div>
  ), [fileUrl, t]);

  /**
   * Estilos del contenedor memoizados
   * @type {Object}
   */
  const containerStyle = useMemo(() => ({ 
    width: '192px', 
    height: '192px' 
  }), []);

  // Early return para loading
  if (isLoading) {
    return <LoadingMC loading={isLoading} />;
  }

  // Para PDFs
  if (isPdf) {
    if (showFallback || !pdfFile) {
      return PdfFallback;
    }

    return (
      <div className="tw-w-48 tw-h-48">
        <PreviewFile 
          selectedFile={pdfFile}
          simplePdf={true}
          containerStyle={containerStyle}
        />
      </div>
    );
  }

  // Para otros archivos
  return FileFallback;
});

FilePreviewMC.displayName = 'FilePreviewMC';

export default FilePreviewMC;